import { v4 as uuidv4 } from "uuid";
import { CompoundFeedDatabase, StageType } from "../../../../../graphql/types"
import {
  BeefBreedingStageDataIntervention,
  BeefGrowingStageDataIntervention,
  BeefInputBreedingIntervention,
  BeefInputGrowingIntervention,
  BeefIntervention,
  BeefOutputBreedingCalvesIntervention,
  BeefOutputIntervention,
  BeefStageIntervention
} from "../../../../sustell_15/models/Intervention/BeefIntervention";
import { createCorePayload, mapResourceUseSustell } from "./outCommonMapperSustellV2";
import { setInterventionFloatValue } from "./outCommonMapperSustell";
import {
  BeefBreedingStageDataInterventionDBModel,
  BeefGrowingStageDataInterventionDBModel,
  BeefInputBreedingInterventionDBModel,
  BeefInputGrowingInterventionDBModel,
  BeefOutputBreedingCalvesDBModel,
  BeefOutputGrowingInterventionDBModel,
  EmissionsDBModel
} from "../../../../sustell_15/models/TempBackendModels/TempInterventionModels/TempBeefInterventionsDB";
import {
  CompoundFeedInfoDBModel as BaselineCompoundFeedInfoDBModel,
  SingleIngredientUseDBModel as BaselineSingleIngredientUseDBModel,
  CattleBeddingDBModel as BaselineCattleBeddingDBModel,
  CattleManureManagementSystemDBModel as BaselineCattleManureManagementSystemDBModel,
} from "../../../../sustell_15/models/TempBackendModels/TempCattleDB"
import {
  CattleAnimalInputDBModel,
  CattleBeddingDBModel,
  CattleHousingAndManureDBModel,
  CattleHousingDBModel,
  CattleManureManagementSystemDBModel,
  CompoundFeedInfoDBModel,
  DietCharacterisation,
  EmissionMitigationDBModel,
  FeedingProgramCalfDBModel,
  FreshGrassDBModel,
  SingleIngredientUseDBModel
} from "../../../../sustell_15/models/TempBackendModels/TempInterventionModels/TempCattleInterventionDB";
import { getDefaultTransportUnit, getUserTransportUnit } from "../../Baseline/mapper/outCommonMapperSustellV2";
import { UserProfilePrefs } from "../../../Helpers/UserProfilePrefs";
import { defaultUnitsV2 } from "../../../../sustell_15/utils/unit-utils";
import {
  CattleEmission,
  CattleFeed,
  CattleHousing,
  CattleHousingAndManure,
  ExternalSource,
  InternalSource,
  MothersMilk
} from "../../../../sustell_15/models/Intervention/CattleIntervention";
import { floatValueInDefaultUnit } from "../../Baseline/mapper/floatHelperFunctions";
import { isFieldFilled } from "../../../../sustell_15/components/FarmFlow/Intervention/CommonDataParts/InterventionGenericInfoDialog";
import { InterventionChangeType } from "../../../../sustell_15/models/TempBackendModels/TempInterventionModels/commons";

const initiBreedingStageData = (): BeefBreedingStageDataInterventionDBModel => {
  return {
    input: {
      animalProductivity: "HIGH",
      averageWeightAtBirth: {},
      permanencePeriod: {},
      startDate: {},
      averageWeightNewAnimals: {},
      animalsPresentAtStart: {},
      animalsPresentAtEnd: {},
      averageWeightAtStart: {},
      internalSources: [],
      externalSources: [],
    },
    feed: {
      cows: {
        databaseFoundation: CompoundFeedDatabase.Gfli,
        dietCharacterisation: "FEEDLOT_CORN",
        averageMilkFat: {},
        mothersMilk: {
          amount: {},
          crudeProteinContent: {},
          digestibility: {},
          dryMatterContent: {}
        },
        compoundFeeds: [],
        singleIngredients: [],
        freshGrass: [],
        compoundFeedsAdditions: [],
        singleIngredientsAdditions: [],
        freshGrassAdditions: [],
      },
      calves: {
        databaseFoundation: CompoundFeedDatabase.Gfli,
        dietCharacterisation: "FEEDLOT_CORN",
        averageMilkFat: {},
        mothersMilk: {
          amount: {},
          crudeProteinContent: {},
          digestibility: {},
          dryMatterContent: {}
        },
        compoundFeeds: [],
        singleIngredients: [],
        freshGrass: [],
        compoundFeedsAdditions: [],
        singleIngredientsAdditions: [],
        freshGrassAdditions: [],
      }
    },
    housing: {
      beddingSystems: [],
      beddingSystemsAdditions: [],
      cows: {
        timeInFeedlot: {},
        timeInHousing: {},
        timeInHousingTied: {},
        timeOnLargeGrazingAreas: {},
        timeOnSmallGrazingAreas: {},
        beddingSystemsAdditions: [],
        manureSystems: [],
        manureSystemsAdditions: [],
      },
      calves: {
        timeInFeedlot: {},
        timeInHousing: {},
        timeInHousingTied: {},
        timeOnLargeGrazingAreas: {},
        timeOnSmallGrazingAreas: {},
        beddingSystemsAdditions: [],
        manureSystems: [],
        manureSystemsAdditions: [],
      }
    },
    output: {
      cows: {
        averageAge: {},
        cowsToAnotherStage: {},
        averageWeightOfCowsLeavingTheStage: {},
        cowsSold: {},
        pricePerCowSold: {},
        numberOfMortalitiesCows: {},
        averageWeightAtMortalityCows: {},
      },
      calves: {
        weanedCalvesToAnotherStage: {},
        averageWeightOfCalvesLeavingTheStage: {},
        weanedCalvesSold: {},
        pricePerWeanedCalfSold: {},
        numberOfMortalitiesCalves: {},
        averageWeightAtMortalityCalves: {},
      }
    },
    emissions: {
      cows: {
        emissionMitigations: [],
      },
      calves: {
        emissionMitigations: [],
      }
    }
  };
};

const initGrowingStageData = (): BeefGrowingStageDataInterventionDBModel => {
  return {
    input: {
      animalProductivity: "HIGH",
      endDate: {},
      isStageRepeated: {},
      growingOperation: {},
      numberOfRepetitions: {},
      growingPurpose: {},
      averageAgeAtStart: {},
      averageWeightAtEnd: {},
      startDate: {},
      averageWeightNewAnimals: {},
      animalsPresentAtStart: {},
      animalsPresentAtEnd: {},
      averageWeightAtStart: {},
      internalSources: [],
      externalSources: []
    },
    feed: {
      databaseFoundation: CompoundFeedDatabase.Gfli,
      dietCharacterisation: "FEEDLOT_CORN",
      averageMilkFat: {},
      compoundFeeds: [],
      singleIngredients: [],
      freshGrass: [],
      compoundFeedsAdditions: [],
      singleIngredientsAdditions: [],
      freshGrassAdditions: [],
    },
    housing: {
      timeInFeedlot: {
        change_type: "" as InterventionChangeType,
        value: null
      },
      timeInHousing: {
        change_type: "" as InterventionChangeType,
        value: null
      },
      timeInHousingTied: {
        change_type: "" as InterventionChangeType,
        value: null
      },
      timeOnLargeGrazingAreas: {
        change_type: "" as InterventionChangeType,
        value: null
      },
      timeOnSmallGrazingAreas: {
        change_type: "" as InterventionChangeType,
        value: null
      },
      beddingSystems: [],
      beddingSystemsAdditions: [],
      manureSystems: [],
      manureSystemsAdditions: [],
    },
    output: {
      averageAge: {},
      cowsToAnotherStage: {},
      averageWeightOfCowsLeavingTheStage: {},
      cowsSold: {},
      pricePerCowSold: {},
      numberOfMortalitiesCows: {},
      averageWeightAtMortalityCows: {}
    },
    emissions: {
      emissionMitigations: [],
    }
  };
};

const initStageData = (stageType: string) => {
  if (stageType === StageType.Breeding)
    return initiBreedingStageData();

  return initGrowingStageData();
};

const mapInternalSources = (
  interventionIntSource: InternalSource[],
  tempIntSource: CattleAnimalInputDBModel[],
) => {
  interventionIntSource?.forEach((item) => {
    const inputData = {
      id: item.id || uuidv4(),
      transportDistance: item.distanceTruck,
      numberOfAnimals: item.numberAnimals,
      origin: item.origin,
      shrinkingRate: item.shrinkingRate,
      originFarmId: item.farmId,
      originFarmName: item.farmName,
      originStageId: item.originStageId,
      originStageName: item.stageName,
      baselineRef: item.baselineRef,
      animalType: item.animalType,
      animalSystemType: item.animalSystemType,
      stageType: item.stageType,
    };

    setInterventionFloatValue(
      inputData,
      "transportDistance",
      item,
      "distanceTruck",
      getUserTransportUnit(item.distanceTruck),
      getDefaultTransportUnit(item.distanceTruck)
    );

    setInterventionFloatValue(
      inputData,
      "numberOfAnimals",
      item,
      "numberAnimals",
    );

    setInterventionFloatValue(
      inputData,
      "shrinkingRate",
      item,
      "shrinkingRate",
    );

    tempIntSource.push(inputData as CattleAnimalInputDBModel);
  });
};

const mapExternalSources = (
  interventionExtSource: ExternalSource[],
  tempExtSource: CattleAnimalInputDBModel[],
) => {
  interventionExtSource?.forEach((item) => {
    const externalData = {
      id: item.id || uuidv4(),
      transportDistance: item.distanceTruck,
      numberOfAnimals: item.numberAnimals,
      origin: item.origin,
      shrinkingRate: item.shrinkingRate,
    };

    setInterventionFloatValue(
      externalData,
      "transportDistance",
      item,
      "distanceTruck"
    );

    setInterventionFloatValue(
      externalData,
      "numberOfAnimals",
      item,
      "numberAnimals"
    );

    setInterventionFloatValue(
      externalData,
      "shrinkingRate",
      item,
      "shrinkingRate"
    );

    tempExtSource.push(externalData as CattleAnimalInputDBModel);
  });
};

const mapCommonInput = (
  interventionInput: BeefInputBreedingIntervention | BeefInputGrowingIntervention,
  tempInput: BeefInputBreedingInterventionDBModel | BeefInputGrowingInterventionDBModel,
) => {
  setInterventionFloatValue(
    tempInput,
    "averageWeightNewAnimals",
    interventionInput,
    "averageWeightNewAnimals"
  );

  setInterventionFloatValue(
    tempInput,
    "animalsPresentAtStart",
    interventionInput,
    "animalsPresentAtStart"
  );

  setInterventionFloatValue(
    tempInput,
    "animalsPresentAtEnd",
    interventionInput,
    "animalsPresentAtEnd"
  );

  setInterventionFloatValue(
    tempInput,
    "averageWeightAtStart",
    interventionInput,
    "averageWeightAtStart"
  );

  if (isFieldFilled(interventionInput["cattleProductivity"])) {
    tempInput["animalProductivity"] = interventionInput["cattleProductivity"] as string;
  }

  mapInternalSources(interventionInput.internalSources, tempInput.internalSources);
  mapExternalSources(interventionInput.externalSources, tempInput.externalSources);
};

const mapBreedingInput = (
  interventionInput: BeefInputBreedingIntervention,
  tempInput: BeefInputBreedingInterventionDBModel
) => {
  const properties: (keyof BeefInputBreedingInterventionDBModel)[] = [
    "averageWeightAtBirth",
    "permanencePeriod"
  ];

  properties.forEach((property) => {
    setInterventionFloatValue(
      tempInput,
      property,
      interventionInput,
      property
    );
  });

  mapCommonInput(interventionInput, tempInput);
};

const mapGrowingInput = (
  interventionInput: BeefInputGrowingIntervention,
  tempInput: BeefInputGrowingInterventionDBModel
) => {
  const properties: (keyof BeefInputGrowingInterventionDBModel)[] = [
    "isStageRepeated",
    "growingOperation",
    "numberOfRepetitions",
    "growingPurpose",
    "averageAgeAtStart",
    "averageWeightAtEnd"
  ];

  properties.forEach((property) => {
    setInterventionFloatValue(
      tempInput,
      property,
      interventionInput,
      property
    );
  });

  mapCommonInput(interventionInput, tempInput);
};

const mapCompoundFeeds = (
  interventionFeed: CattleFeed,
  tempFeed: FeedingProgramCalfDBModel,
  userUOM: any
) => {
  if (interventionFeed.compoundFeeds && Array.isArray(interventionFeed.compoundFeeds)) {
    interventionFeed.compoundFeeds?.forEach((item) => {
      const compoundFeed = {
        compoundFeedId: item.id,
        name: item.feedType,
        quantity: item.kgPerAnimal,
        feedmillLocation: item.origin
      };

      setInterventionFloatValue(
        compoundFeed,
        'quantity',
        item,
        'kgPerAnimal',
        userUOM.unitBarnOutputMass,
        defaultUnitsV2.unitBarnOutputMass
      );

      tempFeed.compoundFeeds?.push(compoundFeed as CompoundFeedInfoDBModel);
    });
  }

  if (interventionFeed.compoundFeedsAdditions && Array.isArray(interventionFeed.compoundFeedsAdditions)) {
    interventionFeed.compoundFeedsAdditions?.forEach(
      (item) => {
        if (item.kgPerAnimal) {
          const value = floatValueInDefaultUnit(
            item.kgPerAnimal,
            userUOM.unitBarnOutputMass,
            defaultUnitsV2.unitBarnOutputMass
          );

          const compoundFeedAddition = {
            name: item.feedType,
            quantity: value,
          };

          tempFeed.compoundFeedsAdditions?.push(compoundFeedAddition as BaselineCompoundFeedInfoDBModel);
        }
      }
    );
  }
};

const mapSingleIngredients = (
  interventionFeed: CattleFeed,
  tempFeed: FeedingProgramCalfDBModel,
  userUOM: any,
) => {
  if (interventionFeed.singleIngredients && Array.isArray(interventionFeed.singleIngredients)) {
    interventionFeed.singleIngredients?.forEach((item) => {
      if (item.kgPerAnimal) {
        const singleIngredient = {
          ingredient: {
            name: item.feedType,
            origin: item.origin
          },
          quantity: {}
        };

        setInterventionFloatValue(
          singleIngredient,
          'quantity',
          item,
          'kgPerAnimal',
          userUOM.unitBarnOutputMass,
          defaultUnitsV2.unitBarnOutputMass
        );

        tempFeed.singleIngredients?.push(singleIngredient as SingleIngredientUseDBModel);
      }
    });
  }

  if (interventionFeed.singleIngredientsAdditions && Array.isArray(interventionFeed.singleIngredientsAdditions)) {
    interventionFeed.singleIngredientsAdditions?.forEach((item) => {
      if (item.kgPerAnimal) {
        const singleIngredientAddition = {
          ingredient: {
            name: item.feedType,
            origin: item.origin
          },
          quantity: floatValueInDefaultUnit(
            item.kgPerAnimal,
            userUOM.unitBarnOutputMass,
            defaultUnitsV2.unitBarnOutputMass
          )
        };

        tempFeed.singleIngredientsAdditions?.push(singleIngredientAddition as BaselineSingleIngredientUseDBModel);
      }
    });
  }
};

const mapFreshGrass = (
  interventionFeed: CattleFeed,
  tempFeed: FeedingProgramCalfDBModel
) => {
  if (tempFeed.freshGrass && Array.isArray(tempFeed.freshGrass)) {
    const properties: (keyof FreshGrassDBModel)[] = [
      "amount",
      "crudeProteinContent",
      "dryMatterContent",
      "digestibility",
      "neutralDetergentFiber",
      "grossEnergyContent",
      "ashContent",
    ];

    interventionFeed.freshGrass?.forEach((item) => {
      const freshGrass = {
        type: item.type as string,
        amount: {},
        crudeProteinContent: {},
        dryMatterContent: {},
        digestibility: {},
        neutralDetergentFiber: {},
        grossEnergyContent: {},
        ashContent: {}
      };

      properties.forEach((property) => {
        setInterventionFloatValue(
          freshGrass,
          property,
          item,
          property
        )
      });

      tempFeed.freshGrass?.push(freshGrass as FreshGrassDBModel);
    });
  }

  if (interventionFeed.freshGrassAdditions && Array.isArray(interventionFeed.freshGrassAdditions)) {
    interventionFeed.freshGrassAdditions?.forEach((item) => {
      const grassAddition = {
        type: item.type,
        amount: floatValueInDefaultUnit(item.amount),
        crudeProteinContent: floatValueInDefaultUnit(item.crudeProteinContent),
        dryMatterContent: floatValueInDefaultUnit(item.dryMatterContent),
        digestibility: floatValueInDefaultUnit(item.digestibility),
        neutralDetergentFiber: floatValueInDefaultUnit(item.neutralDetergentFiber),
        grossEnergyContent: floatValueInDefaultUnit(item.grossEnergyContent),
        ashContent: floatValueInDefaultUnit(item.ashContent),
      };

      tempFeed.freshGrassAdditions?.push(grassAddition);
    });
  }
};


const mapFeed = (
  interventionFeed: CattleFeed,
  tempFeed: FeedingProgramCalfDBModel,
) => {
  const userPrefs = UserProfilePrefs.getInstance();
  const userUOM = userPrefs.getUserUnitPrefs();

  if (isFieldFilled(interventionFeed["dietCharacterisation"])) {
    tempFeed["dietCharacterisation"] = interventionFeed["dietCharacterisation"] as DietCharacterisation;
  }

  setInterventionFloatValue(
    tempFeed,
    "averageMilkFat",
    interventionFeed,
    "averageMilkFat"
  );

  mapCompoundFeeds(interventionFeed, tempFeed, userUOM);
  mapSingleIngredients(interventionFeed, tempFeed, userUOM);
  mapFreshGrass(interventionFeed, tempFeed);
};

const mapCalvesFeed = (
  interventionFeed: CattleFeed,
  tempFeed: FeedingProgramCalfDBModel,
) => {
  if (interventionFeed.mothersMilk) {
    const properties: (keyof MothersMilk)[] = [
      "amount",
      "crudeProteinContent",
      "dryMatterContent",
      "digestibility",
    ];

    properties.forEach((property) => {
      setInterventionFloatValue(
        tempFeed.mothersMilk,
        property,
        interventionFeed.mothersMilk,
        property
      );
    });
  }

  mapFeed(interventionFeed, tempFeed);
};

const mapBeddingType = (
  interventionHousingAndManure: CattleHousingAndManure | CattleHousing,
  tempHousingAndManure: CattleHousingAndManureDBModel | CattleHousingDBModel,
) => {
  const userPrefs = UserProfilePrefs.getInstance();
  const userUOM = userPrefs.getUserUnitPrefs();
  const outputMassPrecision = userPrefs.getUnitOutputMassPrecision();
  const { beddingSystems, beddingSystemsAdditions } = interventionHousingAndManure;

  if (beddingSystems && Array.isArray(beddingSystems)) {
    beddingSystems.forEach((item) => {
      if (item) {
        const beddingType = {
          type: item.beddingType,
          amount: ''
        };

        setInterventionFloatValue(
          beddingType,
          "amount",
          item,
          "beddingAmount",
          userUOM.unitOutputMass,
          defaultUnitsV2.unitOutputMass,
          outputMassPrecision
        );

        tempHousingAndManure?.beddingSystems?.push(beddingType as CattleBeddingDBModel);
      }
    });
  }

  if (
    beddingSystemsAdditions && Array.isArray(beddingSystemsAdditions)) {
    beddingSystemsAdditions.forEach((item) => {
      if (item) {
        const beddingAddition = {
          type: item.beddingType,
          amount: floatValueInDefaultUnit(
            item.beddingAmount,
            userUOM.unitBarnOutputMass,
            defaultUnitsV2.unitBarnOutputMass
          ),
        }

        tempHousingAndManure?.beddingSystemsAdditions?.push(beddingAddition as BaselineCattleBeddingDBModel);
      }
    });
  }
};

const mapMMSType = (
  intHousingAndManure: CattleHousingAndManure,
  tempHousingAndManure: CattleHousingAndManureDBModel,
) => {
  setInterventionFloatValue(
    tempHousingAndManure,
    "timeInHousing",
    intHousingAndManure,
    "timeInHousing",
  );

  setInterventionFloatValue(
    tempHousingAndManure,
    "timeInFeedlot",
    intHousingAndManure,
    "timeInFeedlot",
  );

  setInterventionFloatValue(
    tempHousingAndManure,
    "timeOnLargeGrazingAreas",
    intHousingAndManure,
    "timeGrazingLargeAreas",
  );

  setInterventionFloatValue(
    tempHousingAndManure,
    "timeOnSmallGrazingAreas",
    intHousingAndManure,
    "timeGrazingPastures",
  );

  if (intHousingAndManure.manureSystems && Array.isArray(intHousingAndManure.manureSystems)) {
    intHousingAndManure.manureSystems.forEach((item) => {
      if (item) {
        const mmsType = {
          id: item.id || uuidv4(),
          share: {},
          mmsType: item.mmsType,
          storagePeriod: {},
          manurePhase: {},
        }

        setInterventionFloatValue(
          mmsType,
          "storagePeriod",
          item,
          "storagePeriod",
        );

        setInterventionFloatValue(
          mmsType,
          "share",
          item,
          "share",
        );

        setInterventionFloatValue(
          mmsType,
          "manurePhase",
          item,
          "manurePhase",
        );

        tempHousingAndManure.manureSystems?.push(mmsType as CattleManureManagementSystemDBModel);
      }
    });
  }

  if (
    intHousingAndManure.manureSystemsAdditions && Array.isArray(intHousingAndManure.manureSystemsAdditions)) {
    intHousingAndManure.manureSystemsAdditions.forEach((item) => {
      if (item) {
        const mmsAddition = {
          id: item.id || uuidv4(),
          mmsType: item.mmsType,
          storagePeriod: item.storagePeriod,
          share: floatValueInDefaultUnit(item.share),
          manurePhase: floatValueInDefaultUnit(item.manurePhase),
        };

        tempHousingAndManure.manureSystemsAdditions?.push(mmsAddition as BaselineCattleManureManagementSystemDBModel)
      }
    });
  }
};

const mapHousingAndManure = (
  interventionHousingAndManure: CattleHousingAndManure,
  tempHousingAndManure: CattleHousingAndManureDBModel,
) => {
  mapBeddingType(interventionHousingAndManure, tempHousingAndManure);
  mapMMSType(interventionHousingAndManure, tempHousingAndManure);
}

const mapHousing = (
  interventionHousing: CattleHousing,
  tempHousing: CattleHousingDBModel,
) => {
  mapBeddingType(interventionHousing, tempHousing);
  mapMMSType(interventionHousing.calves, tempHousing.calves);
  mapMMSType(interventionHousing.cows, tempHousing.cows);
};

const mapOutput = (
  interventionOutput: BeefOutputIntervention,
  tempOutput: BeefOutputGrowingInterventionDBModel
) => {
  const properties: (keyof BeefOutputGrowingInterventionDBModel)[] = [
    "averageAge",
    "cowsToAnotherStage",
    "averageWeightOfCowsLeavingTheStage",
    "cowsSold",
    "pricePerCowSold",
    "numberOfMortalitiesCows",
    "averageWeightAtMortalityCows",
  ];

  properties.forEach((property) => {
    setInterventionFloatValue(
      tempOutput,
      property,
      interventionOutput,
      property
    )
  });
};

const mapOutputCalves = (
  interventionOutput: BeefOutputBreedingCalvesIntervention,
  tempOutput: BeefOutputBreedingCalvesDBModel
) => {
  const properties: (keyof BeefOutputBreedingCalvesDBModel)[] = [
    "weanedCalvesToAnotherStage",
    "averageWeightOfCalvesLeavingTheStage",
    "weanedCalvesSold",
    "pricePerWeanedCalfSold",
    "numberOfMortalitiesCalves",
    "averageWeightAtMortalityCalves",
  ];

  properties.forEach((property) => {
    setInterventionFloatValue(
      tempOutput,
      property,
      interventionOutput,
      property
    )
  });
};

const mapEmissions = (
  interventionEmission: CattleEmission,
  tempEmission: EmissionsDBModel,
) => {
  if (!interventionEmission)
    return []

  const emissionEntries = Object.entries(interventionEmission);

  if (!emissionEntries.length)
    return []

  const mappedEmission: EmissionMitigationDBModel[] = [];
  emissionEntries.forEach((item) => {
    if (
      !item[0].includes("_changeMetric") &&
      !item[0].includes("_newValue") &&
      !item[0].includes("original")
    ) {
      const changeType = emissionEntries.find((emission) => {
        emission[0] === `${item[0]}_changeMetric`
      });
      mappedEmission.push({
        emissionType: item[0],
        change: {
          value: item[1],
          change_type: changeType ? changeType[1] : 'relative'
        }
      });
    }
  });

  tempEmission.emissionMitigations = mappedEmission;
};

const mapStageInterventionDataOutSustell = (stageBlockData: BeefStageIntervention) => {
  const { stageData } = stageBlockData;

  const oneStageData = {
    id: stageBlockData?.id || uuidv4(),
    name: stageBlockData?.name,
    type: stageBlockData?.type,
    stageData: { ...initStageData(stageBlockData.type) },
  };

  setInterventionFloatValue(
    stageBlockData,
    "startDate",
    oneStageData,
    "startDate"
  );

  if (stageData) {
    if (stageBlockData.type === StageType.Breeding) {
      const tempBreeding = oneStageData.stageData as BeefBreedingStageDataInterventionDBModel;
      const { input, feed, housing, output, emissions } = stageData as BeefBreedingStageDataIntervention;

      if (input && tempBreeding.input)
        mapBreedingInput(input, tempBreeding.input);

      if (feed && tempBreeding.feed) {
        mapFeed(feed.cows, tempBreeding.feed.cows);
        mapCalvesFeed(feed.calves, tempBreeding.feed.calves);
      }

      if (housing && tempBreeding.housing)
        mapHousing(housing, tempBreeding.housing);

      if (output && tempBreeding.output) {
        if (output.cows && tempBreeding.output.cows)
          mapOutput(output.cows, tempBreeding.output.cows);

        if (output.calves && tempBreeding.output.calves)
          mapOutputCalves(output.calves, tempBreeding.output.calves);
      }

      if (emissions && tempBreeding.emissions) {
        if (emissions.cows && tempBreeding.emissions.cows)
          mapEmissions(emissions.cows, tempBreeding.emissions.cows);

        if (emissions.calves && tempBreeding.emissions.calves)
          mapEmissions(emissions.calves, tempBreeding.emissions.calves);
      }
    }

    if (stageBlockData.type === StageType.Growing) {
      const beefGrowingStageData = oneStageData.stageData as BeefGrowingStageDataInterventionDBModel;
      const { input, feed, housing, output, emissions } = stageData as BeefGrowingStageDataIntervention;

      if (input && beefGrowingStageData.input)
        mapGrowingInput(input, beefGrowingStageData.input);

      if (feed && beefGrowingStageData.feed)
        mapFeed(feed, beefGrowingStageData.feed);

      if (housing && beefGrowingStageData.housing)
        mapHousingAndManure(housing, beefGrowingStageData.housing);

      if (output && beefGrowingStageData.output)
        mapOutput(output, beefGrowingStageData.output);

      if (emissions && beefGrowingStageData.emissions)
        mapEmissions(emissions, beefGrowingStageData.emissions);
    }
  }

  return oneStageData;
};

const mapBeefInterventionDataOutSustell = (formData: BeefIntervention) => {
  let payloadObj = createCorePayload(formData);
  payloadObj = mapResourceUseSustell(formData, payloadObj);

  if (formData.stages && Array.isArray(formData.stages)) {
    return formData.stages.reduce(
      (payload, stage) => {
        if (!stage?.stageData) return payloadObj;

        const mappedStage = mapStageInterventionDataOutSustell(stage);

        return {
          ...payload,
          stages: [
            ...payload.stages,
            {
              ...mappedStage,
              stageData: JSON.stringify(mappedStage.stageData),
            },
          ],
        };
      },
      { ...payloadObj }
    );
  }

  return payloadObj;
};

export default mapBeefInterventionDataOutSustell;
